import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Reveal, SplitText, Magnetic, AnimatedDivider, useParallax } from '../components/effects';
import CTASection from '../components/CTASection';
import ChairmanMessage from '../components/ChairmanMessage';

const HERO_IMG =
  '/site/tarhun-bina-hero.webp';
const KITCHEN_IMG =
  '/site/tarhun-bina-aerial.webp';

const STORY_STATS = [
  { v: '2012', l: 'Kuruluş yılı' },
  { v: '3.200', l: "m² merkezi mutfak" },
  { v: '22', l: 'İzotermli araç filosu' },
  { v: '120+', l: 'Aktif kurumsal müşteri' },
];

const VALUES = [
  {
    eyebrow: 'Misyon',
    title: 'Yemek operasyonunu, sürdürülebilir bir sisteme dönüştürmek.',
    body: 'İdari iş yükünüzü ortadan kaldırmak, yemek konusunu zihninizden tamamen çıkarmak.',
  },
  {
    eyebrow: 'Vizyon',
    title: "Türkiye'de B2B yemek hizmetinin standartlarını belirleyen iş ortağı olmak.",
    body: 'En güvenilir, en şeffaf, en sertifikalı operasyon — sektörel bir referans noktası olmak.',
  },
  {
    eyebrow: 'Değerler',
    title: 'Tutarlılık, dürüstlük, sahiplenme.',
    body: 'Söz verdiğimizi yaparız; yapamayacağımızı söz vermeyiz. Sözleşme bizim için bir başlangıçtır.',
  },
];

const HeroBlock = () => {
  const imgRef = useParallax(0.07);
  return (
    <section className="relative pt-20 lg:pt-28 pb-24 lg:pb-36 overflow-hidden bg-[#F5F3EF]">
      <div className="absolute inset-0 opacity-[0.30] pointer-events-none">
        <img
          ref={imgRef}
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover will-change-transform"
          style={{ transform: 'scale(1.12)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(245,243,239,0.72) 0%, rgba(245,243,239,0.40) 45%, rgba(245,243,239,0.94) 100%)',
          }}
        />
      </div>

      <div className="container-x relative">
        <div className="flex items-center gap-3 mb-7">
          <AnimatedDivider width="3rem" />
          <Reveal delay={100} y={8} duration={700}>
            <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
              Hakkımızda
            </span>
          </Reveal>
        </div>

        <h1
          className="font-display text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.98] tracking-[-0.022em] text-[#1A1A1A] max-w-5xl"
          style={{ fontWeight: 500 }}
        >
          <SplitText text="Bir yemek firması değil," delay={120} />
          <br />
          <span className="italic font-light text-[#9A7F4F]">
            <SplitText text="bir kurumsal sistem." delay={520} />
          </span>
        </h1>

        <Reveal delay={1000}>
          <p className="mt-9 text-[16px] lg:text-[18px] text-[#1A1A1A]/70 max-w-2xl leading-[1.7]">
            2012'den bu yana sanayinin merkezi Kocaeli'de; otomotiv, kimya, ilaç ve enerji
            sektörlerinin günlük yemek operasyonunu yönetiyoruz. Tek hedefimiz —
            müşterimizin yemek konusunu zihninden tamamen çıkarmak.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const StoryBlock = () => {
  const imgRef = useParallax(0.08);

  return (
    <section className="relative py-28 lg:py-44 bg-[#EFEBE3] overflow-hidden">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="relative lg:col-span-6">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-[0_30px_80px_-30px_rgba(114,39,45,0.35)]">
            <img
              ref={imgRef}
              src={KITCHEN_IMG}
              alt="Tarhun Catering merkez tesisi — havadan görünüm"
              className="absolute inset-0 w-full h-full object-cover will-change-transform"
              style={{ transform: 'scale(1.1)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#72272D]/35 via-transparent to-transparent" />

            <div className="absolute bottom-7 left-7 lg:bottom-9 lg:left-9 text-[#F5F0E5]">
              <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A875] mb-2.5 font-medium">
                Merkez Üs · Havadan
              </div>
              <div className="font-display text-[26px] lg:text-[30px] leading-tight tracking-[-0.005em]">
                Kocaeli · 3.200 m²
              </div>
            </div>
          </div>

          <div className="absolute -top-10 -left-2 lg:-left-10 font-display text-[140px] lg:text-[220px] leading-none text-[#9A7F4F]/[0.10] select-none pointer-events-none numeric">
            14
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-7">
            <AnimatedDivider width="3rem" />
            <Reveal delay={100} y={8} duration={700}>
              <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                Hikayemiz
              </span>
            </Reveal>
          </div>

          <Reveal delay={150}>
            <h2
              className="font-display text-[36px] sm:text-[44px] lg:text-[58px] text-[#1A1A1A] leading-[1.06] tracking-[-0.02em]"
              style={{ fontWeight: 500 }}
            >
              Üretim hatlarına yakın bir <span className="italic font-light">mutfak</span>.
              <br className="hidden sm:block" /> Sahanın temposunu bilen bir{' '}
              <span className="italic font-light text-[#9A7F4F]">ekip</span>.
            </h2>
          </Reveal>

          <Reveal delay={350}>
            <div className="mt-8 space-y-5 text-[15.5px] lg:text-[16px] text-[#1A1A1A]/72 leading-[1.75] max-w-xl">
              <p>
                Tarhun Catering, kurucularının fabrika yemekhanesinde yıllarca süren operasyon
                tecrübesinden doğdu. Klasik catering anlayışının kurumsal sahanın gerçeklerine
                uymadığını gördüğümüzde, sıfırdan bir sistem kurmaya karar verdik.
              </p>
              <p>
                Bugün 14 yıllık birikimle; HACCP sertifikalı merkezi mutfak, izotermli filo,
                beslenme uzmanı ve atanmış müşteri yöneticisi modelimizle Türkiye'nin önde
                gelen kuruluşlarına hizmet veriyoruz.
              </p>
              <p className="font-display italic text-[#1A1A1A] text-[19px] lg:text-[22px] leading-[1.4] pt-2 border-t border-[#9A7F4F]/30">
                "Yemek konusunda hiçbir şey duymadığınız gün, biz işimizi iyi yapmışız demektir."
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const FacilityBand = () => {
  const bgRef = useParallax(0.08);
  return (
    <section className="relative w-full overflow-hidden bg-[#0A0C0B]" aria-label="Tarhun Catering merkez tesisi">
      {/* Cinematic full-bleed building image */}
      <div className="relative h-[78vh] min-h-[560px] lg:min-h-[680px] w-full">
        <img
          ref={bgRef}
          src="/site/tarhun-bina-hero.webp"
          alt="Tarhun Catering merkez tesisi — bina ve filo"
          className="absolute inset-0 w-full h-full object-cover will-change-transform"
          style={{ transform: 'scale(1.06)' }}
        />

        {/* Cinematic gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,12,11,0.65) 0%, rgba(10,12,11,0.25) 35%, rgba(10,12,11,0.40) 65%, rgba(10,12,11,0.92) 100%), linear-gradient(90deg, rgba(10,12,11,0.55) 0%, rgba(10,12,11,0.10) 55%, rgba(10,12,11,0) 100%)',
          }}
        />

        {/* Subtle grain pattern */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
            backgroundSize: '4px 4px',
          }}
        />

        {/* Content overlay */}
        <div className="relative z-10 h-full flex items-end pb-16 lg:pb-24">
          <div className="container-x w-full">
            <div className="max-w-3xl">
              <Reveal delay={80} y={10}>
                <div className="flex items-center gap-4 mb-7">
                  <span className="text-[10.5px] numeric tracking-[0.34em] text-[#C9A875] font-medium">
                    KOCAELI · TR
                  </span>
                  <span className="h-px w-10 bg-[#C9A875]/65" />
                  <span
                    className="text-[10.5px] tracking-[0.34em] uppercase font-medium"
                    style={{ color: 'rgba(245,240,229,0.85)' }}
                  >
                    Merkez Üs · Tarhun Catering
                  </span>
                </div>
              </Reveal>

              <Reveal delay={200}>
                <h2
                  className="font-display text-[40px] sm:text-[54px] lg:text-[76px] leading-[1.0] tracking-[-0.022em] text-[#F5F0E5]"
                  style={{ fontWeight: 500, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}
                >
                  Bir adres değil,{' '}
                  <span className="italic font-light text-[#C9A875]">bir operasyon merkezi.</span>
                </h2>
              </Reveal>

              <Reveal delay={500}>
                <p
                  className="mt-7 text-[15.5px] lg:text-[18px] max-w-xl leading-[1.7]"
                  style={{
                    color: 'rgba(245,240,229,0.85)',
                    textShadow: '0 1px 14px rgba(0,0,0,0.35)',
                  }}
                >
                  HACCP sertifikalı 3.200 m² merkez mutfak, 22 araçlık izotermli filo ve 7/24
                  operasyon hattı — Türkiye'nin sanayi kalbinde, sahanın merkezinde konumlandı.
                </p>
              </Reveal>

              {/* Inline mini-stats */}
              <Reveal delay={700}>
                <div className="mt-12 lg:mt-14 grid grid-cols-3 gap-6 lg:gap-12 max-w-2xl">
                  {[
                    { v: '3.200', l: 'm² Merkez Mutfak' },
                    { v: '22', l: 'İzotermli Araç' },
                    { v: '7/24', l: 'Operasyon Hattı' },
                  ].map((s) => (
                    <div key={s.l} className="border-t border-[#C9A875]/40 pt-3.5">
                      <div className="font-display numeric text-[#F5F0E5] text-[28px] lg:text-[38px] leading-none tracking-[-0.02em] font-medium">
                        {s.v}
                      </div>
                      <div className="mt-2.5 text-[9.5px] tracking-[0.28em] uppercase text-[#F5F0E5]/65 font-medium">
                        {s.l}
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        {/* Floating sertifika chip — top right */}
        <div className="absolute top-8 right-8 lg:top-12 lg:right-16 hidden sm:block z-10">
          <Reveal delay={300} y={-10}>
            <div className="px-5 py-3.5 bg-[#0A0C0B]/85 backdrop-blur-sm text-[#F5F0E5] rounded-sm border border-[#C9A875]/30">
              <div className="text-[9.5px] tracking-[0.34em] uppercase text-[#C9A875] font-medium">
                Sertifikalı
              </div>
              <div className="font-display text-[16px] mt-1 leading-tight tracking-[-0.005em]">
                HACCP · ISO&nbsp;22000
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const NumbersBlock = () => {
  return (
    <section className="relative py-28 lg:py-44 bg-[#F5F3EF] overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #1A1A1A 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="container-x relative">
        <div className="max-w-3xl mb-14 lg:mb-20">
          <div className="flex items-center gap-3 mb-7">
            <AnimatedDivider width="3rem" />
            <Reveal delay={100} y={8} duration={700}>
              <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                Operasyonel Ölçek
              </span>
            </Reveal>
          </div>

          <Reveal delay={150}>
            <h2
              className="font-display text-[36px] lg:text-[58px] text-[#1A1A1A] leading-[1.06] tracking-[-0.02em]"
              style={{ fontWeight: 500 }}
            >
              <span className="italic font-light text-[#9A7F4F]">14 yıl</span>, dört rakama
              sığmaz. Ama yine de denedik.
            </h2>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14">
          {STORY_STATS.map((s, i) => (
            <Reveal key={s.l} delay={i * 140} y={28}>
              <div className="border-t border-[#9A7F4F]/35 pt-5">
                <div className="font-display text-[#1A1A1A] numeric text-[44px] sm:text-[58px] lg:text-[72px] leading-none tracking-[-0.025em] font-medium">
                  {s.v}
                </div>
                <div className="mt-4 text-[11.5px] tracking-[0.28em] uppercase text-[#1A1A1A]/65 font-medium">
                  {s.l}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ValuesBlock = () => {
  return (
    <section className="relative py-28 lg:py-44 bg-[#EFEBE3] overflow-hidden">
      <div className="container-x">
        <div className="max-w-3xl mb-14 lg:mb-20">
          <div className="flex items-center gap-3 mb-7">
            <AnimatedDivider width="3rem" />
            <Reveal delay={100} y={8} duration={700}>
              <span className="text-[10.5px] tracking-[0.34em] uppercase text-[#9A7F4F] font-medium">
                Pusulamız
              </span>
            </Reveal>
          </div>

          <Reveal delay={150}>
            <h2
              className="font-display text-[36px] lg:text-[58px] text-[#1A1A1A] leading-[1.06] tracking-[-0.02em]"
              style={{ fontWeight: 500 }}
            >
              Bir <span className="italic font-light">kurumla</span> çalışmak{' '}
              <span className="italic font-light text-[#9A7F4F]">aynı şeyi</span> sürekli yapmak demektir.
            </h2>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#9A7F4F]/22">
          {VALUES.map((v, i) => (
            <Reveal key={v.eyebrow} delay={i * 160}>
              <article className="bg-[#EFEBE3] p-9 lg:p-12 h-full">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-[10.5px] numeric tracking-[0.32em] text-[#9A7F4F] font-medium">
                    0{i + 1}
                  </span>
                  <span className="h-px w-10 bg-[#9A7F4F]/55" />
                  <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#1A1A1A]/65 font-medium">
                    {v.eyebrow}
                  </span>
                </div>
                <h3
                  className="font-display text-[26px] lg:text-[30px] text-[#1A1A1A] leading-[1.18] tracking-[-0.01em]"
                  style={{ fontWeight: 500 }}
                >
                  {v.title}
                </h3>
                <p className="mt-5 text-[14.5px] text-[#1A1A1A]/68 leading-[1.7]">{v.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const VisitInvite = () => {
  return (
    <section className="py-28 lg:py-40 bg-[#F5F3EF] text-center">
      <div className="container-x max-w-3xl mx-auto">
        <Reveal>
          <div className="inline-flex items-center justify-center gap-3 mb-9">
            <span className="h-px w-12 bg-[#9A7F4F]" />
            <span className="text-[10.5px] tracking-[0.35em] uppercase text-[#9A7F4F] font-medium">
              Davetimizdir
            </span>
            <span className="h-px w-12 bg-[#9A7F4F]" />
          </div>
        </Reveal>

        <Reveal delay={150}>
          <h2
            className="font-display text-[40px] lg:text-[64px] text-[#1A1A1A] leading-[1.04] tracking-[-0.02em]"
            style={{ fontWeight: 500 }}
          >
            Tarhun ekibiyle <span className="italic font-light text-[#9A7F4F]">tanışın</span>.
          </h2>
        </Reveal>

        <Reveal delay={350}>
          <p className="mt-7 text-[15.5px] lg:text-[16.5px] text-[#1A1A1A]/68 leading-[1.7] max-w-xl mx-auto">
            Tesisinizi ziyaret edelim, ihtiyacınızı dinleyelim, size özel bir saha analizi
            raporu hazırlayalım. Ücretsiz ve bağlayıcı değildir.
          </p>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-10 flex justify-center">
            <Magnetic strength={0.2}>
              <Link
                to="/iletisim"
                className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F5F3EF] px-8 py-[18px] text-[12px] tracking-[0.22em] uppercase font-medium rounded-sm transition-colors duration-300 hover:bg-[#9A7F4F]"
                data-testid="visit-request-cta"
              >
                Ziyaret Talep Et
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const About = () => {
  return (
    <>
      <ChairmanMessage />
      <HeroBlock />
      <StoryBlock />
      <FacilityBand />
      <NumbersBlock />
      <ValuesBlock />
      <VisitInvite />
      <CTASection />
    </>
  );
};

export default About;
